import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { MenuItem } from 'primereact/menuitem'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { getProxyPersonas, proxyPersonaKeys, type ProxyPersona } from './api/proxyPersonaApi'
import { useProxySwitch } from './useProxySwitch'
import { useCurrentUser } from '../../auth/useCurrentUser'
import { useProxyMode } from './useProxyMode'
import { notifyApiError } from '../../api/errorNotifier'
import { getApiErrorCode } from '../../api/apiError'
import { personaLabelWithAuthority } from './personaLabel'
import './proxy.css'

/** "Kendim" seçeneğinin değeri; gerçek bir kullanıcı kimliğiyle çakışmaması için `null`. */
const SELF = null

/**
 * Vekalet persona seçimi — sağ üstteki **kimlik menüsünün** (`PersonaBadge`) içine eklenen öğeler (089).
 *
 * <p>Kullanıcı önce her zaman kendi kimliğiyle girer; buradan vekili olduğu bir kişiyi seçtiğinde
 * uygulama o kişinin rol, yetki ve veri kapsamıyla çalışmaya başlar.
 *
 * <p><b>Neden kimlik menüsünde, ayrı bir seçim kutusunda değil (2026-08-23):</b> topbar'da etiketsiz
 * bir açılır liste olarak dururken kullanıcılar ne işe yaradığını anlamıyordu — yanındaki dil seçici
 * ve avatar da etiketsizdi, kutunun tek görünür değeri ("Kendim") ise kimsenin sormadığı bir soruya
 * cevap veriyordu. Kimlik değiştirmek kimlik menüsüne ait; menüdeki <b>grup başlığı</b> ("Adına
 * Çalışılacak Kişi") açıklamayı taşır. Bedeli bilinçli kabul edildi: özellik artık bir tık geride,
 * yani daha az keşfedilebilir.
 *
 * <p>Vekaleti olmayan kullanıcıda **boş dizi** döner — menüye hiçbir şey eklenmez (US1-4, sıfır
 * regresyon). Sicili tanımsız olduğu için seçilemeyen vekaletler listeye **konmaz** ama sayılır: grubun
 * sonuna tıklanamaz bir ipucu satırı eklenir. Sessizce eksiltmek, kullanıcının kendisine tanımlandığını
 * bildiği bir vekaleti bulamayıp bunu arıza sanmasına yol açardı (FR-007).
 */
export function useProxyPersonaMenu(): MenuItem[] {
  const { t } = useTranslation()
  const auth = useAuth()
  const { data: me } = useCurrentUser()
  const { active, hasOwnProfile } = useProxyMode()
  const { enterPersona, exitPersona } = useProxySwitch()
  const queryClient = useQueryClient()
  const [switching, setSwitching] = useState(false)

  // `/me` ile aynı koşul.
  const authenticated = auth.isAuthenticated

  const { data: personas } = useQuery({
    queryKey: proxyPersonaKeys.list,
    enabled: authenticated && me != null,
    queryFn: getProxyPersonas,
  })

  if (!personas || personas.length === 0) return []

  // Seçilebilir hiçbir vekalet kalmasa bile grup görünür kalır: sicilsiz hedefler listeye konmaz ama
  // aşağıdaki ipucunda sayılır (FR-007 — sessizce eksiltilmez). Grubun gösterilip gösterilmemesi bu
  // yüzden yalnız yukarıdaki `personas.length === 0` koşuluna bağlıdır.
  const selectable = personas.filter((p) => p.selectable)
  const hiddenCount = personas.length - selectable.length

  // Vekil modunda `me.userId` hedefin kimliğidir — seçili değer doğrudan oradan okunur.
  const selected = active ? (me?.userId ?? SELF) : SELF

  const switchTo = (next: number | null) => {
    if (next === selected) return
    setSwitching(true)
    // Hata durumunda çerez yazılmaz ve mevcut persona korunur. Bildirim **burada** yapılmalıdır:
    // bu çağrı bir TanStack mutation'ı değildir, dolayısıyla global `onError` hunisine düşmez —
    // yakalanmazsa kullanıcı hiçbir şey görmez ve seçimin neden gerçekleşmediğini anlamaz.
    ;(next === SELF ? exitPersona() : enterPersona(next))
      .catch((error: unknown) => {
        // Sunucu "bu personaya geçemezsin" derse (PROXY_TARGET_NOT_AVAILABLE) mevcut persona
        // KORUNUR ve yalnız bir bildirim gösterilir; liste tazelenir ki artık geçerli olmayan
        // seçenek menüden düşsün. Geçersizleşen MEVCUT persona ayrı bir koddur
        // (PROXY_NOT_ACTIVE) ve onu client.ts'teki çapraz-kesen kurtarma ele alır.
        if (getApiErrorCode(error) === 'PROXY_TARGET_NOT_AVAILABLE') {
          queryClient.invalidateQueries({ queryKey: proxyPersonaKeys.list })
        }
        notifyApiError(error)
      })
      .finally(() => setSwitching(false))
  }

  /**
   * Satır etiketi — asistanlıkta **yetki seviyesi de yazılır** (FR-015: Tam/Kısıtlı ayrımı
   * kullanıcıdan gizlenmez).
   *
   * <p>Vekalet satırı eksiz kalır: orada "yetki seviyesi" diye bir kavram yok, parantez eklemek
   * olmayan bir ayrımı varmış gibi gösterirdi. Bilinmeyen tür de eksiz kalır (bkz. `kind` tipi).
   */
  // Etiketleme ORTAK fonksiyondadır: aynı metin seçim sonrası üst bardaki rozette de görünüyor ve
  // iki yerde ayrı yazılsaydı biri güncellenip diğeri unutulurdu (bkz. `personaLabel.ts`).
  const personaLabel = (p: ProxyPersona): string =>
    personaLabelWithAuthority(t, p.displayName, p.kind)

  /** Seçili persona onay imiyle işaretlenir; diğerleri aynı hizada kalsın diye boş ikon yuvası alır. */
  const personaItem = (value: number | null, label: string): MenuItem => ({
    label,
    icon: value === selected ? 'pi pi-check' : 'pi pi-fw',
    disabled: switching,
    command: () => switchTo(value),
  })

  return [
    {
      label: t('proxy.menu.group'),
      items: [
        // "Kendim" YALNIZ kendi profili olan kullanıcıda sunulur (kullanıcı bildirimi 21.10.2026):
        // asistan sisteme girmek için role ihtiyaç duymuyor, yetki kümesi boş kalıyor ve menü her
        // maddeyi süzdüğü için bu satır onu BOMBOŞ bir uygulamaya götürürdü. Ölçüt kipten bağımsız
        // doğru cevabı verir ve eksik alanda fail-open'dır — gerekçe `useProxyMode.hasOwnProfile`'da;
        // kilitlenme takası da orada ve spec FR-017 istisnasında yazılı.
        ...(hasOwnProfile ? [personaItem(SELF, t('proxy.select.self'))] : []),
        ...selectable.map((p) => personaItem(p.userId, personaLabel(p))),
        ...(hiddenCount > 0
          ? [
              {
                label: t('proxy.select.hiddenHint', { count: hiddenCount }),
                // İkon iki iş yapıyor: satırı üstteki personalarla aynı hizaya oturtuyor (ikonsuz
                // öğede PrimeReact ikon yuvasını hiç render etmez → metin sola kayar) ve bunun bir
                // seçenek değil **not** olduğunu söylüyor.
                icon: 'pi pi-fw pi-info-circle',
                disabled: true,
                className: 'proxy-menu-hint',
              },
            ]
          : []),
      ],
    },
  ]
}
